import { commitMutation, useLazyLoadQuery } from 'react-relay';
import { Redirect, usePathParam } from 'react-resource-router';
import { ATLASSIAN_ACCOUNT_USER_QUERY } from 'constants/index';
import { AUTHENTICATE_TRANSACTION_MUTATION } from 'graphql/AuthenticateTransactionMutation';
import { environment } from 'services/relay';
import {
  AuthenticateTransactionMutation,
  AuthenticateTransactionMutation$variables,
} from 'graphql/__generated__/AuthenticateTransactionMutation.graphql';
import { AtlassianAccountUserQuery } from 'graphql/__generated__/AtlassianAccountUserQuery.graphql';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import Loader from 'components/common/loader/Loader';
import { AuthenticateSuccessResponse } from 'constants/types';
import PrimaryAccountModal from './PrimaryAccountModal';
import useStorageTimeStamp from 'hooks/useStorageTimeStamp';
import {
  UpdateTransactionPrimaryMutation,
  UpdateTransactionPrimaryMutation$variables,
} from 'graphql/__generated__/UpdateTransactionPrimaryMutation.graphql';
import { UPDATE_TRANSACTION_PRIMARY_MUTATION } from 'graphql/UpdateTransactionPrimaryMutation';
import { getAuthUrl } from '../../../utils';

const { REACT_APP_ACCOUNT_LINK_BASE_URL, REACT_APP_UPP_BASE_URL } = process.env;

const getRedirectOptionalParams = (email?: string) => {
  const redirectEmail =
    email || window.sessionStorage.getItem('sourceLinkingEmail') || null;
  window.sessionStorage.removeItem('sourceLinkingEmail');
  return redirectEmail ? { email: redirectEmail, prompt: 'none' } : {};
};

const authenticateTransaction = (
  input: AuthenticateTransactionMutation$variables,
): Promise<AuthenticateSuccessResponse> => {
  return new Promise((resolve, reject) => {
    commitMutation<AuthenticateTransactionMutation>(environment, {
      mutation: AUTHENTICATE_TRANSACTION_MUTATION,
      variables: input,
      onCompleted: (response, errors) => {
        const authenticateTransactionRes =
          response?.unified?.linking?.authenticateLinkingWithLoggedInAccount;
        const account1Email = authenticateTransactionRes?.account1?.emailId;

        if (authenticateTransactionRes?.success === false || errors) {
          let errorCode = 'generic_error';
          if (errors) {
            console.error('Initialization of transaction failed', errors);
          } else {
            errorCode =
              authenticateTransactionRes?.errors?.[0]?.code || 'generic_error';
          }
          reject({
            errorCode,
            account1Email,
          });
        } else {
          if (!authenticateTransactionRes?.token) {
            reject({
              errorCode: 'token_not_found',
              account1Email,
            });
          } else {
            resolve(authenticateTransactionRes);
          }
        }
      },
      onError: (error) => {
        console.error('Initialization of transaction failed', error);
        reject({
          errorCode: 'generic_error',
        });
      },
    });
  });
};

const updateTransactionPrimary = (
  input: UpdateTransactionPrimaryMutation$variables,
): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    commitMutation<UpdateTransactionPrimaryMutation>(environment, {
      mutation: UPDATE_TRANSACTION_PRIMARY_MUTATION,
      variables: input,
      onCompleted: (response, errors) => {
        const authenticateTransactionRes =
          response?.unified?.linking?.updateLinkingWithPrimaryAccountAaid;

        if (authenticateTransactionRes?.success === false || errors) {
          let errorCode = 'generic_error';
          if (errors) {
            console.error('Initialization of transaction failed', errors);
          } else {
            errorCode =
              authenticateTransactionRes?.errors?.[0]?.code || 'generic_error';
          }
          reject({
            errorCode,
          });
        } else {
          resolve(authenticateTransactionRes?.success ?? false);
        }
      },
      onError: (error) => {
        console.error('Initialization of transaction failed', error);
        reject({
          errorCode: 'generic_error',
        });
      },
    });
  });
};

const Authenticate: FunctionComponent = () => {
  const [encryptedToken] = usePathParam('token');
  const [authenticationResponse, setAuthenticationResponse] =
    useState<null | AuthenticateSuccessResponse>(null);
  const { setTimeStamp } = useStorageTimeStamp();

  if (
    window.location.origin !== REACT_APP_ACCOUNT_LINK_BASE_URL ||
    !encryptedToken
  ) {
    return <Redirect to={'/error/invalid_path'} />;
  }
  const token = decodeURIComponent(encryptedToken);
  const loggedInUser = useLazyLoadQuery<AtlassianAccountUserQuery>(
    ATLASSIAN_ACCOUNT_USER_QUERY,
    {},
    { fetchPolicy: 'store-or-network' },
  )?.me?.user;

  if (!loggedInUser?.accountId) {
    return <Redirect to={'/error/not_authorized'} />;
  }

  const handlePrimaryAccountSelection = (selectedEmailId: string) => {
    const account1 = authenticationResponse?.account1;
    const account2 = authenticationResponse?.account2;
    const primaryAccountAaid =
      selectedEmailId === account1?.emailId ? account1.aaid : account2?.aaid;

    (async () => {
      try {
        const success = await updateTransactionPrimary({
          token,
          aaid: primaryAccountAaid as string,
        });
        if (success) {
          proceedToCompletion(token, account1?.emailId ?? '');
        } else {
          backToSettings('generic_error', account1?.emailId ?? '');
        }
      } catch (e: any) {
        console.error('Failed to handle primary account selection linking', e);
        const errorCodeReceived = e?.errorCode || 'generic_error';
        backToSettings(errorCodeReceived);
      }
    })();
  };

  const proceedToCompletion = useCallback((token: string, email?: string) => {
    const encryptedAuthenticatedToken = encodeURIComponent(token);
    const completeUrl = `${REACT_APP_UPP_BASE_URL}/user/settings/complete/${encryptedAuthenticatedToken}`;
    if (email) {
      const optionalParams = getRedirectOptionalParams(email);
      const authUrl = getAuthUrl('login', {
        experience: 'entry-auth',
        redirectUrl: encodeURIComponent(completeUrl),
        ...optionalParams,
      });
      window.open(authUrl, '_self');
    } else {
      window.open(completeUrl, '_self');
    }

    setAuthenticationResponse({ success: true });
  }, []);

  const backToSettings = useCallback((errorCode: string, email?: string) => {
    const completeUrl = `${REACT_APP_UPP_BASE_URL}/user/settings?errorCode=${errorCode}`;
    if (email) {
      const optionalParams = getRedirectOptionalParams(email);
      const authUrl = getAuthUrl('login', {
        experience: 'entry-auth',
        redirectUrl: encodeURIComponent(completeUrl),
        ...optionalParams,
      });
      window.open(authUrl, '_self');
    } else {
      window.open(completeUrl, '_self');
    }
    setAuthenticationResponse({ success: true });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setTimeStamp(); // Set timestamp when actual linking starts
        const authenticationResponse = await authenticateTransaction({ token });
        if (
          authenticationResponse.token &&
          (authenticationResponse.primaryAccountIndex === 1 ||
            authenticationResponse.primaryAccountIndex === 2)
        ) {
          proceedToCompletion(
            authenticationResponse.token,
            authenticationResponse.account1?.emailId || '',
          );
        } else {
          setAuthenticationResponse(authenticationResponse);
        }
      } catch (e: any) {
        console.error('Failed to authenticate linking', e);
        const errorCodeReceived = e?.errorCode || 'generic_error';
        const account1Email = e?.account1Email || '';
        backToSettings(errorCodeReceived, account1Email);
      }
    })();
  }, []);

  if (authenticationResponse?.success === false) {
    return <Redirect to={'/error/generic_error'} />;
  }

  if (
    authenticationResponse?.token &&
    authenticationResponse?.primaryAccountIndex !== 1 &&
    authenticationResponse?.primaryAccountIndex !== 2
  ) {
    return (
      <PrimaryAccountModal
        linkedEmails={[
          authenticationResponse.account1?.emailId || '',
          authenticationResponse.account2?.emailId || '',
        ]}
        onPrimaryAccountSelection={handlePrimaryAccountSelection}
        isOpen={true}
      />
    );
  }

  return <Loader />;
};

export default Authenticate;
